// ServiceInvitation.js — "Please join us."
//
// The service program tells people what will happen; this tells them where to
// be, and lets them answer. Three things families ask for and rarely get in
// one place: the invitation, a livestream link for those who cannot travel,
// and an RSVP so they know how many chairs, how much food, how many programs
// to print.
//
// RSVPs deliberately require NO account. The people most likely to attend a
// funeral are the least likely to sign up for anything; every field beyond
// name is optional. Responses live in memorials/{id}/rsvps and are visible in
// full only to the family — a public guest list would expose who is away
// from home on the day of a funeral.
//
// The whole invitation retires itself a day after the service: a memorial
// outlives its service by decades, and stale RSVP forms make a page feel
// abandoned.

import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc, collection, onSnapshot, orderBy, query, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from './firebase';

const GOLD = '#b8964f';

function parseServiceDate(inv) {
  if (!inv?.date) return null;
  const d = new Date(`${inv.date}T${inv.time || '12:00'}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(inv) {
  const d = parseServiceDate(inv);
  if (!d) return '';
  const date = d.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  if (!inv.time) return date;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

function Row({ icon, label, children }) {
  return (
    <div className="flex gap-4 py-3 border-b border-[#f2efe7] last:border-0">
      <div className="w-28 shrink-0 flex items-start gap-2">
        <span aria-hidden="true" className="text-[13px]" style={{ color: GOLD }}>{icon}</span>
        <span className="text-[10px] uppercase pt-0.5" style={{ letterSpacing: '0.2em', color: GOLD }}>
          {label}
        </span>
      </div>
      <div className="flex-1 min-w-0 text-gray-700 font-light text-sm md:text-base leading-relaxed">
        {children}
      </div>
    </div>
  );
}

// --- The RSVP form -----------------------------------------------------------

function RsvpForm({ memorialId, memorialName }) {
  const [name, setName] = useState(auth.currentUser?.displayName || '');
  const [attending, setAttending] = useState('yes');
  const [guests, setGuests] = useState(0);
  const [email, setEmail] = useState(auth.currentUser?.email || '');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please tell us your name.'); return; }
    setBusy(true);
    setError('');
    try {
      await addDoc(collection(db, 'memorials', memorialId, 'rsvps'), {
        name: name.trim().slice(0, 80),
        attending,                       // 'yes' | 'no' | 'virtual'
        guests: Math.max(0, Math.min(20, Number(guests) || 0)),
        email: email.trim().slice(0, 120),
        phone: phone.trim().slice(0, 40),
        note: note.trim().slice(0, 280),
        userId: auth.currentUser?.uid || null,
        createdAt: serverTimestamp(),
      });
      setDone(true);
    } catch (err) {
      console.error('RSVP failed:', err);
      setError('That could not be sent. Please try again.');
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8">
        <div
          className="w-12 h-12 mx-auto rounded-full flex items-center justify-center text-lg mb-4"
          style={{
            background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)',
            color: '#ffe2a8',
            boxShadow: '0 0 0 1px rgba(255,215,140,0.5), 0 0 20px rgba(255,215,140,0.25)',
          }}
        >
          ✦
        </div>
        <p className="text-gray-700 font-light">
          {attending === 'no'
            ? 'Thank you for letting the family know.'
            : `Thank you — the family will be glad you're coming.`}
        </p>
        <button
          onClick={() => { setDone(false); setName(''); setGuests(0); setNote(''); setBusy(false); }}
          className="mt-4 text-[11px] uppercase text-gray-400 hover:text-black transition-colors"
          style={{ letterSpacing: '0.2em' }}
        >
          Respond for someone else
        </button>
      </div>
    );
  }

  const field = 'w-full px-3 py-2.5 border border-[#eee9df] bg-white focus:outline-none focus:border-[#cbab6a] transition-colors text-sm';
  const lab = 'block text-[10px] uppercase mb-1.5';
  const labStyle = { letterSpacing: '0.22em', color: '#96793c' };

  return (
    <form onSubmit={submit} className="max-w-lg mx-auto">
      <p className="text-center text-[10px] uppercase mb-5" style={{ letterSpacing: '0.3em', color: GOLD }}>
        Will you be there?
      </p>

      <label className={lab} style={labStyle}>Your name</label>
      <input className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className={lab} style={labStyle}>Attending</label>
          <select className={field} value={attending} onChange={(e) => setAttending(e.target.value)}>
            <option value="yes">Yes, in person</option>
            <option value="virtual">Yes, joining online</option>
            <option value="no">Sadly, I cannot</option>
          </select>
        </div>
        <div>
          <label className={lab} style={labStyle}>Guests with you</label>
          <select className={field} value={guests} onChange={(e) => setGuests(e.target.value)} disabled={attending === 'no'}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>{n === 0 ? 'Just me' : `+${n}`}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className={lab} style={labStyle}>Email <span className="normal-case tracking-normal text-gray-400">(optional)</span></label>
          <input className={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </div>
        <div>
          <label className={lab} style={labStyle}>Phone <span className="normal-case tracking-normal text-gray-400">(optional)</span></label>
          <input className={field} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" />
        </div>
      </div>

      <label className={`${lab} mt-4`} style={labStyle}>
        A word for the family <span className="normal-case tracking-normal text-gray-400">(optional)</span>
      </label>
      <textarea
        className={`${field} resize-none`}
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={`Anything ${memorialName ? `${memorialName}'s family` : 'the family'} should know`}
      />

      {error && <p className="mt-3 text-sm text-red-600 text-center">{error}</p>}

      <div className="text-center mt-6">
        <button type="submit" disabled={busy} className="sm-btn-primary disabled:opacity-50">
          {busy ? 'Sending…' : 'Send my reply'}
        </button>
        <p className="mt-2 text-[11px] text-gray-400 font-light">
          No account needed — your reply goes only to the family.
        </p>
      </div>
    </form>
  );
}

// --- The family's view of who is coming --------------------------------------

function GuestList({ memorialId }) {
  const [rsvps, setRsvps] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!memorialId) return undefined;
    const q = query(collection(db, 'memorials', memorialId, 'rsvps'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => setRsvps(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn('rsvp listener:', err)
    );
    return unsub;
  }, [memorialId]);

  const tally = useMemo(() => {
    let inPerson = 0; let online = 0; let cannot = 0;
    rsvps.forEach((r) => {
      if (r.attending === 'no') cannot += 1;
      else if (r.attending === 'virtual') online += 1 + (r.guests || 0);
      else inPerson += 1 + (r.guests || 0);
    });
    return { inPerson, online, cannot };
  }, [rsvps]);

  if (rsvps.length === 0) return null;

  return (
    <div className="mt-8 pt-6 border-t border-[#eee9df]">
      <div className="text-center">
        <p className="text-[10px] uppercase mb-2" style={{ letterSpacing: '0.3em', color: GOLD }}>
          For the family
        </p>
        <p className="text-gray-700 font-light">
          <strong className="font-normal" style={{ color: '#96793c' }}>{tally.inPerson}</strong> coming in person
          {tally.online > 0 && <> · <strong className="font-normal" style={{ color: '#96793c' }}>{tally.online}</strong> joining online</>}
          {tally.cannot > 0 && <> · {tally.cannot} sending love from afar</>}
        </p>
        <button
          onClick={() => setOpen((v) => !v)}
          className="mt-3 text-[11px] uppercase text-gray-500 hover:text-black transition-colors"
          style={{ letterSpacing: '0.2em' }}
        >
          {open ? 'Hide the guest list' : `See all ${rsvps.length} replies`}
        </button>
      </div>

      {open && (
        <div className="mt-5 max-w-lg mx-auto divide-y divide-[#f2efe7] border border-[#eee9df] bg-[#fdfbf6]">
          {rsvps.map((r) => (
            <div key={r.id} className="px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-gray-800">
                  {r.name}{r.guests > 0 ? ` +${r.guests}` : ''}
                </span>
                <span
                  className="text-[10px] uppercase shrink-0"
                  style={{
                    letterSpacing: '0.18em',
                    color: r.attending === 'no' ? '#a09a92' : r.attending === 'virtual' ? '#7d96c2' : '#96793c',
                  }}
                >
                  {r.attending === 'no' ? 'Cannot attend' : r.attending === 'virtual' ? 'Online' : 'Attending'}
                </span>
              </div>
              {(r.email || r.phone) && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {[r.email, r.phone].filter(Boolean).join(' · ')}
                </p>
              )}
              {r.note && <p className="text-[13px] text-gray-600 italic mt-1">"{r.note}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- The invitation ----------------------------------------------------------

function ServiceInvitation({ memorial, memorialId, isFamily }) {
  const inv = memorial?.serviceInvitation;
  const first = memorial?.firstName || 'them';

  const hasAnything = !!(inv && (inv.message || inv.venue || inv.address || inv.date || inv.livestreamUrl));
  const when = formatWhen(inv);

  // A service passes; the memorial remains. One day after, the invitation
  // becomes a record rather than a request.
  const serviceDate = parseServiceDate(inv);
  const isPast = serviceDate ? (Date.now() > serviceDate.getTime() + 24 * 3600 * 1000) : false;
  const rsvpOpen = !!(inv?.rsvpEnabled !== false) && !isPast;

  if (!hasAnything) return null;

  return (
    <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-8">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: GOLD }}>
          {isPast ? 'The Service' : 'You Are Invited'}
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          {isPast ? `Remembering ${first}` : 'Please Join Us'}
        </h2>
        <div
          className="mx-auto mt-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
      </div>

      {inv.message && (
        <p className="max-w-2xl mx-auto text-center text-gray-700 font-light leading-relaxed whitespace-pre-wrap mb-8">
          {inv.message}
        </p>
      )}

      <div className="max-w-2xl mx-auto">
        {(inv.venue || inv.address) && (
          <Row icon="◈" label="Location">
            {inv.venue && <div className="text-gray-800">{inv.venue}</div>}
            {inv.address && <div className="whitespace-pre-line">{inv.address}</div>}
            {inv.address && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${inv.venue || ''} ${inv.address}`.trim())}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-[11px] uppercase hover:underline"
                style={{ letterSpacing: '0.18em', color: '#96793c' }}
              >
                Get directions →
              </a>
            )}
          </Row>
        )}

        {when && <Row icon="✧" label="Date & time">{when}</Row>}

        {inv.livestreamUrl && (
          <Row icon="▷" label="Watch online">
            <a
              href={inv.livestreamUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: '#96793c' }}
            >
              Join the livestream →
            </a>
            <p className="text-[12px] text-gray-400 mt-1">For those who cannot be there in person.</p>
          </Row>
        )}

        {inv.dressCode && <Row icon="❖" label="Dress">{inv.dressCode}</Row>}
      </div>

      {rsvpOpen && (
        <div className="mt-8 pt-8 border-t border-[#eee9df]">
          <RsvpForm memorialId={memorialId} memorialName={memorial?.firstName} />
        </div>
      )}

      {isFamily && <GuestList memorialId={memorialId} />}
    </div>
  );
}

export default ServiceInvitation;
